import type { IPriceProductIssue, IPriceProductStatusSignals } from '../utils';
import type { Campaign } from '~/modules/campaign/types';

import { usePlatformProductsQuery } from '~/api/graphql/queries/platformProducts.generated';
import { resolvePriceProductIssue, toPlatformProductIdsQuery } from '../utils';
import { PLATFORM_PRODUCTS_MAX_PAGE_SIZE } from '~/constants/platformProduct';
import { createContext, useContext, useEffect, useMemo } from 'react';
import { STORE_PLATFORM } from '~/constants/storePlatform';
import { captureSentryError } from '~/exceptions/sentry';
import { CAMPAIGN_TYPE } from '~/constants/campaign';

interface IListingPriceCampaign {
  type?: string | null;
  variants?: Array<{ price?: { baseID?: string | null } | null } | null | undefined> | null;
}

interface ICampaignPriceListingProductContext {
  titleByBaseId: Record<string, string>;
  /** Missing a baseID entirely (rather than mapping it to `undefined`) is this map's own way of saying
   * "not fetched yet" — a row renders no issue until its provider has an actual answer. */
  signalsByBaseId: Record<string, IPriceProductStatusSignals>;
}

const CampaignPriceListingProductContext =
  createContext<ICampaignPriceListingProductContext | null>(null);

// baseID is optional since callers pass it straight from `variant.price?.baseID`; falls back to the
// stored title (via the caller's own `??`) when there's no provider in the tree or no live match yet.
export const useCampaignPriceListingProductTitle = (baseID?: string | null): string | undefined => {
  const context = useContext(CampaignPriceListingProductContext);
  return baseID ? context?.titleByBaseId[baseID] : undefined;
};

export interface IUseCampaignPriceListingProductIssueParams {
  campaign: Campaign;
}

export interface IUseCampaignPriceListingProductIssueResult {
  issue?: IPriceProductIssue;
}

/** Reads the issue off the same batched listing fetch the row's title comes from, instead of a
 * one-by-one re-fetch per row — see usePriceProductStatus for the single-campaign equivalent. */
export const useCampaignPriceListingProductIssue = ({
  campaign,
}: IUseCampaignPriceListingProductIssueParams): IUseCampaignPriceListingProductIssueResult => {
  const context = useContext(CampaignPriceListingProductContext);

  const issue = useMemo(() => {
    if (!context) return undefined;

    return resolvePriceProductIssue({
      campaign,
      getSignal: (baseID) => context.signalsByBaseId[baseID],
    });
  }, [context, campaign]);

  return { issue };
};

export interface CampaignPriceListingProductProviderProps {
  // Only the campaigns actually rendered on screen (the current paginated page), not the whole store —
  // this fetches live product titles for exactly what's visible, not every price test that ever ran.
  campaigns?: IListingPriceCampaign[];
  children: React.ReactNode;
}

export const CampaignPriceListingProductProvider: React.FC<
  CampaignPriceListingProductProviderProps
> = ({ campaigns, children }) => {
  const baseIDs = useMemo(() => {
    const ids = (campaigns ?? [])
      .filter((campaign) => campaign.type === CAMPAIGN_TYPE.PRICE)
      .flatMap((campaign) => campaign.variants ?? [])
      .map((variant) => variant?.price?.baseID)
      .filter((baseID): baseID is string => !!baseID);

    return [...new Set(ids)];
  }, [campaigns]);

  const { data, error, isPreviousData } = usePlatformProductsQuery(
    {
      parameter: {
        first: Math.min(baseIDs.length, PLATFORM_PRODUCTS_MAX_PAGE_SIZE),
        query: toPlatformProductIdsQuery(baseIDs),
      },
      platform: STORE_PLATFORM.SHOPIFY,
    },
    {
      enabled: baseIDs.length > 0,
      // Picking/removing a price rewrites baseIDs; previous data keeps untouched rows from
      // flickering back to their stored title while the new set is in flight.
      keepPreviousData: true,
      // 'always' keeps titles honest after a rename in Shopify admin; plain `true` never fires
      // because the global staleTime in app/root.tsx is 7 days.
      refetchOnWindowFocus: 'always',
    },
  );

  // Falling back to the stored title is fine for the user; going blind is not — a persistent
  // failure is otherwise indistinguishable from "the title never changed".
  useEffect(() => {
    if (!error) return;

    captureSentryError(error, {
      source: 'CampaignPriceListingProductProvider',
      level: 'warning',
      tags: { experiment: 'Experiment' },
      extra: { baseIDCount: baseIDs.length },
    });
  }, [error, baseIDs.length]);

  const titleByBaseId = useMemo(() => {
    const map: Record<string, string> = {};

    data?.platformProducts.edges?.forEach((edge) => {
      const product = edge?.node;
      if (product?.baseID && product.title) map[product.baseID] = product.title;
    });

    return map;
  }, [data]);

  // Only built once the fetch has actually landed: a baseID absent from the response is read as deleted,
  // which would misfire against every row while the query is still in flight — including while
  // keepPreviousData still serves the last page's response for a new set of baseIDs.
  const signalsByBaseId = useMemo(() => {
    const map: Record<string, IPriceProductStatusSignals> = {};
    if (!data) return map;

    const foundBaseIds = new Set<string>();
    data.platformProducts.edges?.forEach((edge) => {
      const product = edge?.node;
      if (!product?.baseID) return;

      foundBaseIds.add(product.baseID);
      map[product.baseID] = {
        status: product.status,
        isDeleted: false,
        isPublished: !!(product.onlineStoreURL || product.publishedAt),
      };
    });

    if (isPreviousData) return map;

    baseIDs.forEach((baseID) => {
      if (!foundBaseIds.has(baseID)) map[baseID] = { isDeleted: true, isPublished: false };
    });

    return map;
  }, [data, isPreviousData, baseIDs]);

  const value = useMemo(
    () => ({ titleByBaseId, signalsByBaseId }),
    [titleByBaseId, signalsByBaseId],
  );

  return (
    <CampaignPriceListingProductContext.Provider value={value}>
      {children}
    </CampaignPriceListingProductContext.Provider>
  );
};
